import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { api } from "../lib/api";
import { bandColor } from "../lib/bands";
import { Sliders, RotateCcw } from "lucide-react";

const PILLARS = [
  { key: "personal", label: "Personal Stability", color: "#DDA15E" },
  { key: "professional", label: "Professional Momentum", color: "#FF8A00" },
  { key: "financial", label: "Financial Health", color: "#5C6B4E" },
];

export function WhatIfSimulator({ baseScores, baseFit, decisionContext }) {
  const original = {
    personal: baseScores.personal,
    professional: baseScores.professional,
    financial: baseScores.financial,
  };
  const [vals, setVals] = useState(original);
  const [sim, setSim] = useState({ core: baseScores.core, band: baseScores.band, fit: baseFit });
  const timer = useRef(null);

  const changed =
    vals.personal !== original.personal ||
    vals.professional !== original.professional ||
    vals.financial !== original.financial;

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      try {
        const { data } = await api.post("/simulate", {
          ...vals,
          decision: decisionContext.decision,
          timeline: decisionContext.timeline,
          risk: decisionContext.risk,
        });
        setSim(data);
      } catch (e) {
        /* ignore */
      }
    }, 220);
    return () => timer.current && clearTimeout(timer.current);
  }, [vals]); // eslint-disable-line

  const coreDelta = +(sim.core - baseScores.core).toFixed(1);
  const fitDelta = +(sim.fit.fit - baseFit.fit).toFixed(1);
  const coreColor = bandColor(sim.band);
  const fitColor = bandColor(sim.fit.fit_band);

  const Delta = ({ v }) => {
    if (v === 0) return <span className="text-stone/60 font-mono text-xs">±0</span>;
    const up = v > 0;
    return (
      <span className={`font-mono text-xs ${up ? "text-moss" : "text-[#B0554D]"}`}>
        {up ? "▲" : "▼"} {Math.abs(v)}
      </span>
    );
  };

  return (
    <div className="mt-6 card-soft p-8" data-testid="what-if-simulator">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Sliders size={18} className="text-brand-orange" />
          <h2 className="font-display font-bold text-xl text-ink">What-if simulator</h2>
        </div>
        {changed && (
          <button data-testid="simulator-reset" onClick={() => setVals(original)} className="flex items-center gap-1.5 text-xs text-stone hover:text-ink transition-colors duration-300">
            <RotateCcw size={13} /> Reset
          </button>
        )}
      </div>
      <p className="text-sm text-stone mb-8">Drag a pillar to see how nurturing it would move your Scorecaster Score and decision fit.</p>

      <div className="grid lg:grid-cols-2 gap-8">
        <div className="space-y-7">
          {PILLARS.map((p) => (
            <div key={p.key} data-testid={`sim-slider-${p.key}`}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-ink">{p.label}</span>
                <div className="flex items-center gap-3">
                  <Delta v={+(vals[p.key] - original[p.key]).toFixed(1)} />
                  <span className="font-mono text-sm" style={{ color: p.color }}>{Math.round(vals[p.key])}</span>
                </div>
              </div>
              <input type="range" min="0" max="100" value={vals[p.key]} onChange={(e) => setVals({ ...vals, [p.key]: Number(e.target.value) })} className="w-full" style={{ accentColor: p.color }} />
            </div>
          ))}
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="rounded-2xl border border-clay bg-sand p-6 flex flex-col justify-center">
            <span className="label-tiny">Projected core</span>
            <div className="flex items-baseline gap-2 mt-2">
              <motion.span key={sim.core} className="font-mono text-4xl font-extrabold" style={{ color: coreColor }}>{sim.core}</motion.span>
              <Delta v={coreDelta} />
            </div>
            <span className="label-tiny mt-1" style={{ color: coreColor }}>{sim.band}</span>
          </div>
          <div className="rounded-2xl border border-clay bg-sand p-6 flex flex-col justify-center">
            <span className="label-tiny">Projected fit</span>
            <div className="flex items-baseline gap-2 mt-2">
              <motion.span key={sim.fit.fit} className="font-mono text-4xl font-extrabold" style={{ color: fitColor }}>{sim.fit.fit}</motion.span>
              <Delta v={fitDelta} />
            </div>
            <span className="label-tiny mt-1" style={{ color: fitColor }}>{sim.fit.probability}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
